/**
 * ReadOnlyValueR — locked display for a system-managed field value.
 *
 * Used for fields flagged `read_only` in the schema: the value is set by the
 * system (e.g. relationship status comes from the matter's relationship
 * basis, changed only via the staff Overview switch) and must never be edited
 * inline, so the form and the matter cannot drift apart.
 */
import { useFormContext } from 'react-hook-form';
import { Lock } from 'lucide-react';
import type { FieldDef, RendererProps } from '../types';
import { resolveLabel, resolveHelpText, deriveFieldName } from '../utils';

interface Props extends RendererProps {
  field: FieldDef;
}

export function ReadOnlyValueR({ field, formId, namePrefix, audience }: Props) {
  const { watch } = useFormContext();
  const name = deriveFieldName(field, namePrefix);
  const label = resolveLabel(field, formId, audience);
  const raw = watch(name);
  const value = typeof raw === 'string' && raw.trim() !== '' ? raw : null;
  // Resolve an option label when the stored value is a code.
  const display = field.options?.find((o) => o.value === value)?.label ?? value;
  const hint = resolveHelpText(field, audience);
  return (
    <div>
      <span className="form-label flex items-center gap-1.5">
        {label}
        <Lock size={12} className="text-slate-400" />
      </span>
      <div className="form-input bg-slate-50 text-slate-600 cursor-not-allowed select-none">
        {display ?? <span className="italic text-slate-400">Not set</span>}
      </div>
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}
